// AUTO-GENERATED by scripts/snapshot-demo-data.mjs — do not edit by hand.
//
// Static fixtures for the no-backend demo build: a verbatim snapshot of the
// live Supabase database, one JSON file per relation, taken 2026-09-04.
//
// ⚠️  These files contain REAL, UNSCRUBBED PRODUCTION DATA. See ./README.md.
//
// Each table sits behind a lazy dynamic import() so Vite code-splits it into
// its own chunk — a tab only downloads the tables it actually reads. Resolved
// tables are cached, so repeat reads never re-fetch.

type Rows = Record<string, unknown>[];
type Loader = () => Promise<{ default: Rows }>;

/**
 * Every relation in the snapshot: the 104 distinct string literals passed to
 * `.from('…')` anywhere under `src/`, plus `equipment`, `gmb_reviews` and
 * `invoices`, which are only ever reached through a runtime variable.
 */
const RELATIONS = [
  'adjuster_communications', 'adjustment_stats', 'adjustments', 'alert_leads',
  'app_feedback', 'ar_snapshots_weekly', 'bdm_budget_settings',
  'bdm_onboarding_progress', 'bdm_onboarding_runs', 'bdm_weekly_reviews',
  'billing_qa_checks', 'call_log', 'capacity_settings', 'changelog_entries',
  'claims', 'collection_contacts', 'collections_exclusions',
  'commission_confirmations', 'compensation_plans', 'compliance_tasks',
  'compliance_trigger_events', 'compliance_workflows', 'coverage_rules',
  'customers', 'damage_causes', 'dashboard_page_views', 'denial_library',
  'divisions', 'equipment', 'equipment_assignments', 'equipment_inventory',
  'equipment_loadout', 'equipment_readings', 'error_log', 'estimates',
  'external_contact_notes', 'external_contacts', 'gmb_reviews',
  'insurance_carriers', 'invoices', 'job_boxes', 'job_dates', 'job_divisions',
  'job_documents', 'job_fees', 'job_invoices', 'job_labor_costs', 'job_notes',
  'job_participants', 'job_photos', 'job_rooms', 'job_scope_items',
  'job_size_factors', 'job_statuses', 'job_tasks', 'job_work_predictions',
  'job_workflow_state', 'jobs', 'lead_channels', 'lead_sources',
  'marketing_leads', 'measurable_audits', 'measurable_owners',
  'measurable_snapshots', 'meeting_attendance', 'meeting_headlines',
  'meeting_issues', 'meeting_template_segments', 'meetings',
  'migration_column_presets', 'migration_imports', 'mold_appointments',
  'note_grades', 'notifications', 'offices', 'ops_manual_measurables',
  'payments', 'playbook_pages', 'plumber_contacts', 'plumbers', 'profiles',
  'qb_storage_billed', 'rebuild_communications', 'rebuild_planner_assignments',
  'rebuttals', 'rocks', 'roles', 'sync_flags', 'team_members', 'teams',
  'time_clock_snapshots', 'truck_loadout', 'truck_loadout_checks', 'trucks',
  'user_todos', 'v_carrier_insights', 'v_equipment_inventory_status',
  'v_job_active_participants', 'v_job_compliance_summary', 'v_jobs_full',
  'v_referral_jobs', 'warehouse_areas', 'warehouse_assignments',
  'warehouse_bins', 'warehouse_events', 'work_day_assignments', 'work_day_crew',
  // Never read via `.from()` — only ever served by a Pages Function — so the
  // first snapshot pass missed them. The demo API router (src/demo/api) reads
  // them for /api/seats, /api/knowledge, /api/meetings and /api/rocks.
  'org_seats', 'seat_assignments', 'knowledge_folders', 'knowledge_docs',
  'meeting_templates', 'rock_milestones',
] as const;

/**
 * One lazy loader per relation.
 *
 * The specifier is a template literal rather than 107 hand-written literal
 * paths on purpose. Vite's dynamic-import-vars resolves `./tables/${x}.json`
 * statically (it globs `./tables/*.json`; a same-directory glob is refused by the plugin) and emits a separate chunk per file, so
 * code-splitting is identical — but TypeScript never resolves the individual
 * JSON modules. That matters: letting `resolveJsonModule` infer literal types
 * for ~28MB of fixture JSON added ~45s to every `tsc -b` run.
 */
const LOADERS: Record<string, Loader> = Object.fromEntries(
  RELATIONS.map((name) => [
    name,
    (() => import(`./tables/${name}.json`)) as Loader,
  ]),
);

/** Every table name present in the snapshot. */
export const DEMO_TABLE_NAMES: readonly string[] = Object.freeze([...RELATIONS]);

/** Resolved (and in-flight) tables, keyed by relation name. */
const cache = new Map<string, Promise<Rows>>();

/** Lazily loads a table's rows. Unknown table -> []. */
export function loadTable(name: string): Promise<Rows> {
  const hit = cache.get(name);
  if (hit) return hit;

  const loader = LOADERS[name];
  if (!loader) return Promise.resolve([]);

  const p = loader().then((m) => (Array.isArray(m.default) ? m.default : []));
  // Don't poison the cache if a chunk fails to load.
  p.catch(() => cache.delete(name));
  cache.set(name, p);
  return p;
}
